import { ApiError } from "./api";
import { importsApi, type Compression, type UploadInfo } from "./imports-api";

/** Original bytes per chunk: small enough for any proxy, large enough to keep request overhead low. */
export const CHUNK_SIZE = 16 * 1024 * 1024;
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];
const SPEED_WINDOW_MS = 20_000;

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
  storedBytes: number;
  bytesPerSecond: number | null;
  secondsLeft: number | null;
}

export function canCompress(): boolean {
  return typeof CompressionStream !== "undefined";
}

/** Plain dumps are compressed in the browser; files that are already gzip are sent as they are. */
export function compressionFor(file: File): Compression {
  return !/\.gz$/i.test(file.name) && canCompress() ? "gzip" : "none";
}

export function isDumpFile(name: string): boolean {
  return /\.(sql|sql\.gz|gz)$/i.test(name);
}

async function gzip(blob: Blob): Promise<Blob> {
  return new Response(blob.stream().pipeThrough(new CompressionStream("gzip"))).blob();
}

/** SHA-256 in hex, or null where Web Crypto is unavailable (plain HTTP outside localhost). */
async function sha256(blob: Blob): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer()));
  return Array.from(digest, (b) => b.toString(16).padStart(2, "0")).join("");
}

interface PreparedChunk {
  index: number;
  body: Blob;
  sha: string | null;
}

function prepare(file: File, upload: UploadInfo, index: number): Promise<PreparedChunk> {
  const start = index * upload.chunk_size;
  const slice = file.slice(start, Math.min(start + upload.chunk_size, file.size));
  return (async () => {
    const body = upload.compression === "gzip" ? await gzip(slice) : slice;
    return { index, body, sha: await sha256(body) };
  })();
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}

/** Errors worth retrying: no answer at all, or the server/proxy temporarily unavailable. */
function transient(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 && error.status !== 507;
  return error instanceof TypeError;
}

/**
 * Send `file` as the chunks of `upload`, starting from `upload.next_chunk` (so an interrupted upload resumes),
 * then complete it. The next chunk is compressed while the current one is being sent. Aborting `signal`
 * pauses: calling again with the latest UploadInfo continues where the server left off.
 */
export async function sendFile(
  file: File,
  upload: UploadInfo,
  { signal, onProgress }: { signal?: AbortSignal; onProgress?: (progress: UploadProgress) => void } = {},
): Promise<UploadInfo> {
  const chunks = Math.max(1, Math.ceil(file.size / upload.chunk_size));
  const samples: { at: number; bytes: number }[] = [];
  let current = upload;
  let next: Promise<PreparedChunk> | null =
    current.next_chunk < chunks ? prepare(file, current, current.next_chunk) : null;

  const report = () => {
    const now = performance.now();
    samples.push({ at: now, bytes: current.received_bytes });
    while (samples.length > 2 && now - (samples[0]?.at ?? now) > SPEED_WINDOW_MS) samples.shift();
    const first = samples[0] ?? { at: now, bytes: current.received_bytes };
    const elapsed = (now - first.at) / 1000;
    const speed = elapsed > 0.5 ? (current.received_bytes - first.bytes) / elapsed : null;
    onProgress?.({
      sentBytes: current.received_bytes,
      totalBytes: file.size,
      storedBytes: current.stored_bytes,
      bytesPerSecond: speed,
      secondsLeft: speed ? (file.size - current.received_bytes) / speed : null,
    });
  };
  report();

  while (next) {
    const chunk = await next;
    signal?.throwIfAborted();
    const following = chunk.index + 1 < chunks ? prepare(file, current, chunk.index + 1) : null;
    for (let attempt = 0; ; attempt++) {
      try {
        current = await importsApi.putChunk(current.id, chunk.index, chunk.body, chunk.sha, signal);
        break;
      } catch (error) {
        if (signal?.aborted) throw signal.reason;
        if (error instanceof ApiError && error.code === "chunk_out_of_order") {
          // The server is somewhere else (another tab, a lost answer): continue from where it is.
          const expected = Number(error.detail.expected);
          current = { ...current, next_chunk: expected };
          break;
        }
        const delay = RETRY_DELAYS_MS[attempt];
        if (!transient(error) || delay === undefined) throw error;
        await wait(delay, signal);
      }
    }
    report();
    if (current.next_chunk >= chunks) next = null;
    else if (current.next_chunk === chunk.index + 1 && following) next = following;
    else next = prepare(file, current, current.next_chunk);
  }
  return importsApi.completeUpload(current.id);
}

const RESUME_KEY = "tv.imports.uploads";

function remembered(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

/** Remember which local file (by modification time) an upload belongs to, to resume it safely later. */
export function rememberUpload(upload: UploadInfo, file: File): void {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify({ ...remembered(), [upload.id]: file.lastModified }));
  } catch {
    // storage unavailable: resuming still works by name and size
  }
}

/** The unfinished upload this file can continue, if any: same name, size and (when known) modification time. */
export function resumableUpload(file: File, uploads: UploadInfo[]): UploadInfo | null {
  const known = remembered();
  return (
    uploads.find(
      (u) =>
        u.status === "uploading" &&
        u.original_name === file.name &&
        u.source_size === file.size &&
        u.compression === compressionFor(file) &&
        (known[u.id] === undefined || known[u.id] === file.lastModified),
    ) ?? null
  );
}
